import {Link, useSearchParams} from "react-router-dom";

import notFoundImage from "../../../assets/images/not-found.png";
import {useLanguage} from "../../../context/LanguageProvider";
import {ROUTES} from "../../../shared/constants/routes";
import StatusMessage from "../../../shared/components/elements/StatusMessage";
import BlogCard from "../components/BlogCard";
import PageLayout from "../../../shared/components/layout/PageLayout";
import PageTitle from "../../../shared/components/layout/PageTitle";
import SEO from "../../../shared/components/layout/SEO";
import WrapperBlog from "../../../shared/components/layout/WrapperBlog";
import Pagination from "../../../shared/components/layout/Pagination";
import {useBlogs} from "../hooks/useBlogs";

const BlogListPage = () => {
    const {lang, t} = useLanguage();
    const [searchParams, setSearchParams] = useSearchParams();

    // The page lives in the URL (?page=2): back button, refresh and shared links all keep it
    const page = Math.max(1, Number(searchParams.get("page")) || 1);
    const {blogs, totalPages, loading, error} = useBlogs(page);

    const handlePageChange = (newPage: number) => {
        // Page 1 is the default, so it gets a clean URL
        setSearchParams(newPage === 1 ? {} : {page: String(newPage)});
        window.scrollTo({top: 0});
    };

    const renderContent = () => {
        if (loading) return <StatusMessage>{t.blog.loading}</StatusMessage>;
        if (error) return <StatusMessage>{t.blog.loadFailed}</StatusMessage>;

        if (blogs.length === 0) {
            return (
                <div className="w-full relative mb-20">
                    <img src={notFoundImage} alt="" className="w-1/2 m-auto"/>
                    <p className="text-3xl text-center my-5">{t.blog.not_found}</p>
                </div>
            );
        }

        return (
            <>
                <div className="flex items-center mb-10 leading-4 lg:leading-2 px-3 md:px-1">
                    <PageTitle title={t.blog.title}/>
                </div>

                <div className="grid grid-cols-1 p-3 lg:p-0 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full">
                    {blogs.map((blog) => (
                        <article key={blog.id} className="w-full">
                            <Link to={ROUTES.blogPost(lang, blog.slug)}>
                                <BlogCard blog={blog}/>
                            </Link>
                        </article>
                    ))}
                </div>

                <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange}/>
            </>
        );
    };

    return (
        <>
            <SEO title={t.blog.title} description={t.blog.description} path="/blog"/>

            <PageLayout mainClassName="flex-1">
                <WrapperBlog className="my-10">{renderContent()}</WrapperBlog>
            </PageLayout>
        </>
    );
};

export default BlogListPage;